<template>
  <AutoCompleteShell
    ref="autocomplete"
    label="Requester"
    name="Requester"
    variable-height
    :disabled="isDisabled || !canModifyRequester"
    :error-messages="remoteErrors"
    @close="onClose"
    @close-expand="clearSearch"
    @clear="clear"
    @update:search="updateSearch"
  >
    <template #popover>
      <picker-list-virtualized ref="virtualizedList" :items="items">
        <template #item="{ item }">
          <picker-list-item
            :item="item"
            :is-selected="isSelected(item)"
            :prepend-icon="false"
            dense
            class="py-1"
            @select="selectItem"
          />
        </template>
      </picker-list-virtualized>
      <div v-if="noResultsMessage" class="no-data">
        <v-container class="my-4">
          <v-row justify="center">
            <vs-text center type="subdued">{{ noResultsMessage }}</vs-text>
          </v-row>
        </v-container>
      </div>
      <vs-button
        v-if="canCreateNewGuestRequester"
        full-width
        type="secondary"
        icon="mdi-plus"
        label="New requester"
        class="force-slight-overflow"
        rectangular
        remove-invisible-border
        @click.stop="handleCreateClick"
      />
    </template>
  </AutoCompleteShell>
</template>

<script setup lang="ts">
import { useStore } from 'vuex-composition-helpers'
import { computed, nextTick, onMounted, ref, toRef } from 'vue'
import User from '@/models/user'
import AutoCompleteShell from '@/components/vision/next/inputs/AutoCompleteShell.vue'
import PickerListItem from '@/components/vision/next/picker-list/PickerListItem.vue'
import PickerListVirtualized from '@/components/vision/next/picker-list/PickerListVirtualized.vue'
import {
  HeadlessAutoCompleteValueType,
  ValueResolver,
} from '@/components/vision/next/inputs/useAutoComplete'
import VsButton from '@/components/vision/VsButton.vue'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'
import { useJobRequesterPicker } from '@/components/tickets/JobRequesterPicker/JobRequesterPicker'
import VsText from '@/components/vision/VsText.vue'
import { useFormField } from '@/components/vision/next/forms/useFormAdapter'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'

const props = withDefaults(
  defineProps<{
    customerSid?: string | null
    isDisabled: boolean
    jobHubId: string
  }>(),
  {
    customerSid: null,
  },
)

const customerSidAsRef = toRef(props, 'customerSid')
const jobHubIdAsRef = toRef(props, 'jobHubId')
const { canCreateNewGuestRequester } = useBusinessUnitPermissions()
const store = useStore()
const canModifyRequester = computed(() => store.getters['permissions/canModifyRequester'])
const emit = defineEmits<{
  (e: 'autofill', field: string, value: string): void
  (e: 'selected', id: any): void
}>()
const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()
const handleCreateClick = () => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isNewGuestUserBottomSheetVisible: true,
  })
  autocomplete.value?.close()
}
const isSelected = (item: HeadlessAutoCompleteValueType) => {
  return item.id === customerSidAsRef.value
}
const getUserBySid = computed<(id: string | null) => User | null | undefined>(
  () => store.getters['users/getBySid'],
)
const autocomplete = ref()
const virtualizedList = ref()
const onNavigate = () => {
  autocomplete.value?.input?.focus()
}
const scrollToPosition = (pos: number) => {
  virtualizedList?.value?.scrollToPosition(pos)
}
const valueResolver = async (id: string | null) => {
  const user = getUserBySid.value(id)
  if (user) return { id: user.id, title: user.name }
  return { id: null, title: null }
}

const onValueChanged = (incoming?: HeadlessAutoCompleteValueType) => {
  emit('selected', incoming?.id)
  if (incoming) {
    autocomplete.value?.close()
  }
}

const { items, total, updateSearch, selectItem, clear, clearSearch, noResultsMessage } =
  useJobRequesterPicker({
    onNavigate,
    scrollToPosition,
    valueResolver: valueResolver as ValueResolver,
    onValueChanged,
    value: customerSidAsRef,
    jobHubId: jobHubIdAsRef,
  })

onMounted(() => {
  if (total.value === 1 && items.value[0]?.id) {
    emit('autofill', 'requestor', items.value[0].id as string)
  }
})

const { allErrorMessages: remoteErrors, runValidation } = useFormField({
  value: customerSidAsRef,
  required: true,
})

const onClose = () => {
  nextTick().then(() => runValidation())
}
</script>
